import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BTN_LG } from '../constants/buttons';
import { apiPost } from '../utils/api';
import useSubmitGuard from '../hooks/useSubmitGuard';
import { useAuth } from '../contexts/AuthContext';
import usePageViewLogger from '../hooks/usePageViewLogger';
import { FIELD_LABEL_CLASS } from '../constants/inputs';

// Common SFX/film-production department names. Click-to-fill quick presets.
// Casting only one of them as "first dept"; admin can add more later via
// Admin Settings → Departments.
const DEPT_PRESETS = [
  'SFX', 'Props', 'Costume', 'Camera', 'Stunts',
  'Art Department', 'Production', 'Sound', 'Lighting',
];

// The seat the creator takes in the department they are creating.
//
// Product owner, 2026-08-05: "buyers and supervisors should both be able to sign
// up as either but obviously cannot join another department as that role unless
// its given too them. Signing up roles (buyer/supervisor) are strictly for that
// department that they are creating."
//
// ⚠️ THESE TWO ROLES ARE NOT A HIERARCHY and the copy must never imply one. The
// product owner has been explicit that seniority is a human fact, not a
// permissions fact — neither is "above" the other, and they hold the same
// capability columns today (docs/ROLES.md §B). So each option describes THE JOB,
// not the rights and not the rank. Do not reorder these to suggest a ladder, and
// do not add wording like "full access" or "senior" to either.
//
// The value is sent to POST /api/auth/onboarding/create-production, which
// re-validates it against its own frozen allowlist. This list is a UI
// convenience; it is NOT the gate. Adding an entry here grants nothing — the
// server refuses anything outside SETUP_ROLES with a 400.
const SEAT_ROLE_OPTIONS = [
  {
    value: 'buyer',
    // "Coordinator" here is a LABEL only (product owner, 2026-10-08): coordinators
    // take the buyer seat until a real Coordinator role exists. The value stays
    // 'buyer' — there is no coordinator row in role_definitions.
    label: 'Buyer/Coordinator',
    description: "You handle this department's buying — raising orders, suppliers, POs and deliveries.",
  },
  {
    value: 'supervisor',
    label: 'Supervisor',
    description: "You run this department's work — crew, planning and sign-off, alongside whoever is buying.",
  },
];

export default function OnboardingCreateProduction() {
  // 🚨 LOGS ITS OWN PAGE VIEW (2026-09-16) — this route is auth-required but is
  // rendered OUTSIDE `Layout` on purpose (full-screen wizard, no sidebar), so
  // the single `usePageViewLogger()` in `Layout.js` never covered it. It is the
  // FIRST thing a self-signup buyer ever does, and it recorded nothing, which
  // makes the one funnel step worth measuring the one step that was invisible.
  // ⚠️ Declared in `App.pageViewCoverage.test.js`; do not remove the call
  // without moving the route under `Layout` or declaring it skipped there.
  usePageViewLogger();
  useEffect(() => { document.documentElement.classList.add('dark'); }, []);

  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const [step, setStep] = useState(1);
  const [productionName, setProductionName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [firstDeptName, setFirstDeptName] = useState('');
  // Defaults to 'buyer' — the seat every self-signup user took before this
  // choice existed, so an untouched wizard behaves exactly as it always has.
  const [seatRole, setSeatRole] = useState('buyer');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const step1Valid = productionName.trim().length > 0 && companyName.trim().length > 0;
  const step2Valid = firstDeptName.trim().length > 0;

  const handleNext = (e) => {
    e.preventDefault();
    setError('');
    if (!step1Valid) { setError('Production name and company name are both required.'); return; }
    setStep(2);
  };

  const handleBack = () => { setError(''); setStep(1); };

  // ── Double-submit guard (2026-08-13, `hooks/useSubmitGuard.js`). One gate
  // for both writes on this wizard: `submitting` is a state flag set before
  // the first await (the fast-double-click hole — a doubled submit is two
  // productions), and `handleSkip` had no in-flight state at all (a doubled
  // skip fired `POST /onboarding/skip` twice and raced two `refreshAuth`es).
  const { guard: guardOnboarding } = useSubmitGuard();

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!step2Valid) { setError('Please name your first department.'); return; }
    return guardOnboarding(async () => {
    setSubmitting(true);
    try {
      const res = await apiPost('/api/auth/onboarding/create-production', {
        production_name: productionName.trim(),
        company_name: companyName.trim(),
        first_department_name: firstDeptName.trim(),
        seat_role: seatRole,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not create production');

      // Persist the new department as the active one for the post-redirect
      // dashboard. DepartmentContext auto-selects via localStorage when there
      // are multiple assignments (personal + new shared).
      try {
        localStorage.setItem('sfx_active_department_id', String(data.department_id));
        // Clear the signup intent locally — the wizard is done. (The server
        // already NULLs user.signup_intent; this clears the client copy so the
        // stranded-buyer rescue in DashboardRedirect can't loop us back here.)
        localStorage.removeItem('sfx_signup_intent');
      } catch {}

      // Refresh the auth user so user.signup_intent is re-read as null (the
      // server cleared it). Without this the cached user still carries the old
      // intent and DashboardRedirect bounces us back into the wizard.
      try { await refreshAuth(); } catch { /* non-fatal — DashboardRedirect also guards on activeRole */ }

      // Trigger DepartmentContext to refetch assignments and switch into the
      // newly-created production immediately.
      window.dispatchEvent(new Event('sfx-role-change'));

      // Land on dashboard (DashboardRedirect routes a buyer to /department/dashboard).
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
    });
  };

  const handleSkip = () => guardOnboarding(async () => {
    // User decided to use their personal space instead. Clear the signup intent
    // (server + local) and refresh auth so DashboardRedirect's stranded-buyer
    // rescue doesn't loop them straight back into this wizard.
    try {
      await apiPost('/api/auth/onboarding/skip', {});
      localStorage.removeItem('sfx_signup_intent');
      await refreshAuth();
    } catch { /* best-effort — proceed to dashboard regardless */ }
    navigate('/', { replace: true });
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            CREW FORGE
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Set up your production
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Step {step} of 2
            </span>
            <div className="flex gap-1">
              <span className={`h-1.5 w-8 rounded-full ${step >= 1 ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
              <span className={`h-1.5 w-8 rounded-full ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handleNext} className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Your production</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Tell us about the show or project you're working on. You can rename these later.
                </p>
              </div>

              <div>
                <label htmlFor="productionName" className={FIELD_LABEL_CLASS}>
                  Production name
                </label>
                <input
                  id="productionName" type="text" required autoFocus
                  value={productionName}
                  onChange={(e) => setProductionName(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="e.g. The Burning Shore"
                  maxLength={200}
                />
              </div>

              <div>
                <label htmlFor="companyName" className={FIELD_LABEL_CLASS}>
                  Company name
                </label>
                <input
                  id="companyName" type="text" required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="e.g. Acme Films Ltd"
                  maxLength={200}
                />
              </div>

              <button type="submit" disabled={!step1Valid} className={`${BTN_LG.primary} w-full justify-center`}>
                Continue
              </button>

              <button type="button" onClick={handleSkip} className="w-full text-center text-xs text-gray-500 dark:text-gray-400 hover:underline">
                Skip — just use a personal space for now
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Your first department</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Name it and tell us what you do here. Add more departments from Admin Settings later.
                </p>
              </div>

              <div>
                <label htmlFor="firstDeptName" className={FIELD_LABEL_CLASS}>
                  Department name
                </label>
                <input
                  id="firstDeptName" type="text" required autoFocus
                  value={firstDeptName}
                  onChange={(e) => setFirstDeptName(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="e.g. SFX"
                  maxLength={100}
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Quick picks:</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {DEPT_PRESETS.map(name => (
                    <button
                      key={name} type="button"
                      onClick={() => setFirstDeptName(name)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition ${
                        firstDeptName === name
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-blue-400'
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={FIELD_LABEL_CLASS}>
                  What's your role in this department?
                </label>
                <div className="space-y-2">
                  {SEAT_ROLE_OPTIONS.map(opt => (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                        seatRole === opt.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <input
                        type="radio"
                        name="seatRole"
                        value={opt.value}
                        checked={seatRole === opt.value}
                        onChange={(e) => setSeatRole(e.target.value)}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{opt.label}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {opt.description}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  This applies to this department only. Joining another department as
                  either role is something its own team gives you.
                </p>
              </div>

              <div className="flex gap-2">
                <button type="button" onClick={handleBack} className="px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                  Back
                </button>
                <button type="submit" disabled={!step2Valid || submitting} className={`${BTN_LG.primary} flex-1 justify-center`}>
                  {submitting ? 'Creating production...' : 'Create production'}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-gray-400 dark:text-gray-500">
          Need help? <Link to="/privacy-policy" className="hover:underline">Privacy</Link>{' · '}
          <Link to="/terms-of-service" className="hover:underline">Terms</Link>
        </p>
      </div>
    </div>
  );
}
